import axios from "axios";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";

const AddUsages = ({
  show,
  handleClose,
  editMode,
  selectedUsages,
  onAddSuccess,
  onUpdateSuccess, // New prop for update success
  existingUsages, // New prop to receive existing data for client-side check
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedCategorie, setSelectedCategorie] = useState("");
  const [categories, setCategories] = useState([]);
  const [nom, setNom] = useState("");

  useEffect(() => {
    // Reset form fields when modal is shown for adding new or for editing
    if (editMode && selectedUsages) {
      setNom(selectedUsages.libelleParametre || "");
      setSelectedCategorie(selectedUsages.categorie?.categorieId || "");
    } else {
      setNom("");
      setSelectedCategorie("");
    }

    // Fetch categories regardless of edit mode
    axios
      .get(`${API_URL}/api/categorie`, {
        headers: {
          Authorization: `Bearer ${TOKEN}`,
        },
      })
      .then((res) => {
        setCategories(res.data);
      })
      .catch((err) => {
        console.error(err); // Use console.error for errors
        toast.error("Erreur lors de la récupération des catégories");
      });
  }, [editMode, selectedUsages, show]); // Added 'show' to dependency array

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const trimmedNom = nom.trim();
    const selectedCat = categories.find(
      (cat) => cat.categorieId === parseInt(selectedCategorie, 10)
    );

    // --- Duplicate Check Logic ---
    if (!editMode) {
      // For adding, check against all existing usages
      const isDuplicate = existingUsages.some(
        (item) =>
          item.libelleParametre.trim().toLowerCase() === trimmedNom.toLowerCase() &&
          item.categorie?.categorieId === selectedCat?.categorieId
      );

      if (isDuplicate) {
        toast.warn("Un usage avec ce nom et cette catégorie existe déjà.");
        setLoading(false);
        return;
      }
    } else {
      // For editing, check against existing usages EXCLUDING the one being edited
      const isDuplicate = existingUsages.some(
        (item) =>
          item.parametreId !== selectedUsages.parametreId && // Exclude the current item
          item.libelleParametre.trim().toLowerCase() === trimmedNom.toLowerCase() &&
          item.categorie?.categorieId === selectedCat?.categorieId
      );

      if (isDuplicate) {
        toast.warn("Un autre usage avec ce nom et cette catégorie existe déjà.");
        setLoading(false);
        return;
      }
    }
    // --- End Duplicate Check Logic ---

    const usageData = {
      ...selectedUsages, // Keep existing ID for PUT request
      libelleParametre: trimmedNom,
      typeParametre: "USAGE",
      categorie: selectedCat || null, // Ensure category is null if not found
    };

    try {
      const response = editMode
        ? await axios.put(`${API_URL}/api/parametre`, usageData, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          })
        : await axios.post(`${API_URL}/api/parametre`, usageData, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          });

      toast.success(editMode ? "Usage modifié avec succès" : "Usage ajouté avec succès");

      if (!editMode) {
        onAddSuccess(response.data); // For adding to the top of the list
      } else {
        onUpdateSuccess(response.data); // Call new update success handler
      }
      handleClose(); // Close modal after success
    } catch (err) {
      console.error(err);
      // More specific error handling if API provides it
      if (err.response && err.response.status === 409) { // Assuming 409 for conflict/duplicate from API
        toast.error("Erreur : Cet usage existe déjà sur le serveur.");
      } else {
        toast.error("Erreur lors de l'enregistrement de l'usage.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
          aria-label="Close"
        ></button>
        <div className="text-center mb-4">
          <h4 className="address-title mb-2">
            {editMode ? "Modifier l'usage" : "Ajouter un usage"}
          </h4>
          <p className="address-subtitle">
            {editMode ? "Modifier l'usage existant" : "Ajouter un nouvel usage"}
          </p>
        </div>
        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-12">
            <label className="form-label" htmlFor="usagesName">Nom</label>
            <input
              type="text"
              id="usagesName"
              onChange={(e) => setNom(e.target.value)}
              className="form-control"
              placeholder="Nom des usages"
              name="nom"
              value={nom}
              required
            />
          </div>
          <div className="col-12">
            <label className="form-label" htmlFor="usagesCategory">Catégorie</label>
            <select
              id="usagesCategory"
              name="usagesCategory"
              className="select2 form-select"
              onChange={(e) => setSelectedCategorie(e.target.value)}
              value={selectedCategorie}
              required
            >
              <option value="">Sélectionner la catégorie</option>
              {categories.map((cat) => (
                <option key={cat.categorieId} value={cat.categorieId}>
                  {cat.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 text-center">
            <button
              type="submit"
              className="btn btn-primary me-3"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-grow spinner-grow-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button
              type="button"
              className="btn btn-label-secondary"
              onClick={handleClose}
            >
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddUsages;